// ===== BEGIN FILE: src/utils/cachedUser.ts =====
/**
 * Global user cache to prevent repeated supabase.auth.getUser() calls
 * across widgets and components
 */

import { supabase } from './supabaseClient';
import type { User } from '@supabase/supabase-js';

let cachedUser: User | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Get current user with caching
 * Returns cached user if available and recent, otherwise fetches from Supabase
 */
export async function getCachedUser(): Promise<User | null> {
  const now = Date.now();
  
  // Return cached user if available and recent
  if (cachedUser && (now - cacheTimestamp < CACHE_DURATION_MS)) {
    return cachedUser;
  }
  
  // Fetch from Supabase
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) {
      console.error('[getCachedUser] Error:', error);
      return null;
    }
    
    cachedUser = user;
    cacheTimestamp = now;
    return user;
  } catch (err) {
    console.error('[getCachedUser] Exception:', err);
    return null;
  }
}

/**
 * Clear user cache (call on logout)
 */
export function clearUserCache(): void {
  cachedUser = null;
  cacheTimestamp = 0;
}

// ===== END FILE: src/utils/cachedUser.ts =====
